import { fmt, todayStr } from "./dateHelpers";
import { NOTIF_GRACE_MS, DEFAULT_ALARM_SOUND } from "../constants/misc";
import { reminderLabel } from "./reminderFormat";
import { isIntervalDueFrom } from "./habitInterval";
import { isGoogleTaskEvent } from "../google/googleTask";

// An item with no clock time still has to ring at SOME hour on the reminder
// day, so day/week-before reminders anchor to 9:00 AM — the same convention
// Google Calendar uses for all-day events. Offsets shorter than a day ("at
// start", "10 min before", …) are meaningless without a real time and are
// skipped for untimed items; the add/edit panel only offers day/week offsets
// in that case anyway (see ItemPanel in CalendarExtras.jsx).
export const UNTIMED_REMINDER_HOUR = "09:00";
const DAY_MINUTES = 1440;

// ─── OCCURRENCE MEMOIZATION ───────────────────────────────────────────────────
// The calendar recomputes "does this item occur on this date?" for every visible
// day × every item, inline in render. With recurring tasks that explodes into
// thousands of calls, and every button tap re-renders the whole calendar — so
// taps felt laggy. We cache results keyed on the item *object* via a WeakMap.
// Because item updates are immutable (editing a task produces a new object), an
// edited item misses the cache and recomputes automatically, while unchanged
// items reuse their results across the many re-renders a single tap triggers.
// Pure function of (item fields, dateStr) → safe to memoize.
export const _occCache = new WeakMap();      // event/reminder object → Map<dateStr, bool>
export const _taskOccCache = new WeakMap();  // task object          → Map<dateStr, bool>

// ─── GOOGLE-SYNCED RECURRING EVENT CASCADE DELETE ─────────────────────────────
// Google Calendar sync (src/google/sync.js) imports recurring events with
// `singleEvents: true`, so each occurrence comes back as its own standalone
// Google event with a shared `recurringEventId` — sync.js imports every one of
// those as an independent, unrelated local calEvent (no local recurrence rule
// links them). So deleting "one" only ever removed that single instance; the
// rest — same series, different local ids — were untouched. This removes every
// local event that shares the deleted one's `gcalRecurringId`, so the whole
// series disappears at once. Purely-local recurring events (frequencyType) are
// unaffected — they're already one shared object with no siblings to cascade to.
// A Google Task synced in from Google Calendar is never removed here: Clarity has
// no permission to delete it on Google, so the next sync would only bring it back
// (Jade, 2026-10-20). It goes once it's deleted in Google Tasks (src/google/sync.js).
export function removeEventCascade(events, id) {
  const target = events.find(e => e.id === id);
  if (isGoogleTaskEvent(target)) return events;
  const cascadeId = target?.gcalRecurringId;
  return events.filter(e => e.id !== id && (!cascadeId || e.gcalRecurringId !== cascadeId));
}
export function removeEventsCascade(events, ids) {
  let next = events;
  ids.forEach(id => { next = removeEventCascade(next, id); });
  return next;
}

export function _cachedOccur(cache, compute, item, dateStr) {
  if (!item || typeof item !== "object") return compute(item, dateStr);
  let byDate = cache.get(item);
  if (byDate) {
    const hit = byDate.get(dateStr);
    if (hit !== undefined) return hit;
  } else {
    byDate = new Map();
    cache.set(item, byDate);
  }
  const result = compute(item, dateStr);
  byDate.set(dateStr, result);
  return result;
}

export function isOccurringOn(item, dateStr) {
  return _cachedOccur(_occCache, _computeOccurringOn, item, dateStr);
}

export function isTaskOccurringOn(task, dateStr) {
  return _cachedOccur(_taskOccCache, _computeTaskOccurringOn, task, dateStr);
}

// ─── RECURRING EVENT / REMINDER HELPER ───────────────────────────────────────
// Returns true if a calendar item (event or reminder) should appear on dateStr,
// taking into account its repeat setting.
// repeatDays indices: 0=Mon … 6=Sun  (matches the day-picker order)
// JS Date.getDay():  0=Sun, 1=Mon … 6=Sat  → convert: monBased = (jsDay + 6) % 7
export function _computeOccurringOn(item, dateStr) {
  // Reminders (which have no external per-occurrence override map like tasks/
  // events do) record a moved/skipped single occurrence directly on themselves
  // — see CalendarTab.jsx's Skip/Reschedule flow. Checked first so a moved-to
  // date shows up regardless of the base date/recurrence pattern below, and a
  // moved-from or skipped date never does.
  if (item.skippedOccurrences && item.skippedOccurrences.includes(dateStr)) return false;
  if (item.movedOccurrences && item.movedOccurrences.length) {
    if (item.movedOccurrences.some(m => m.from === dateStr)) return false;
    if (item.movedOccurrences.some(m => m.to === dateStr)) return true;
  }

  if (!item.date) return false;                  // no base date set
  if (dateStr < item.date) return false;         // before the event was created
  if (item.repeatEnd && dateStr > item.repeatEnd) return false; // past the recurrence end date

  // ── New frequencyType (set via QuickAdd) takes priority over legacy repeat ──
  const ft = item.frequencyType;
  if (ft && ft !== "once") {
    if (ft === "daily") return true;
    // Every N days / weeks / months from the start date (habitInterval.js;
    // mirrored in lib/interval.js for the server push).
    if (ft === "interval") return isIntervalDueFrom(item, item.date, dateStr);
    if (ft === "specific" || ft === "count") {
      // "specific": repeat on chosen days of week (Mon=0…Sun=6)
      // "count": no specific days stored, just show on base date each week
      const d = new Date(dateStr + "T00:00:00");
      const monBased = (d.getDay() + 6) % 7; // 0=Mon…6=Sun
      const days = item.frequencyDays || [];
      if (ft === "specific") return days.length === 0 ? true : days.includes(monBased);
      // count: treat as "any day" — the count limit is advisory, not a date filter
      return true;
    }
    if (ft === "monthly") {
      const d = new Date(dateStr + "T00:00:00");
      if (item.monthlyMode === "count") return true; // any day of month
      const dates = item.monthDates || [];
      return dates.length === 0 ? d.getDate() === new Date(item.date + "T00:00:00").getDate() : dates.includes(d.getDate());
    }
    if (ft === "yearly") {
      // Specific calendar dates that recur every year (e.g. birthdays),
      // stored as `${month}-${day}` (1-based, no zero-pad) — same as habits.
      const d = new Date(dateStr + "T00:00:00");
      const md = `${d.getMonth() + 1}-${d.getDate()}`;
      if (!(item.yearDates || []).includes(md)) return false;
      // "this year only" (yearlyOnce): match only in the base date's year.
      if (item.yearlyOnce) return d.getFullYear() === new Date(item.date + "T00:00:00").getFullYear();
      return true;
    }
  }

  // ── Legacy repeat field ───────────────────────────────────────────────────
  const repeat = item.repeat || "None";

  if (repeat === "None") return item.date === dateStr;

  if (repeat === "Daily") return true;

  if (repeat === "Weekly") {
    const days = item.repeatDays || [];
    if (days.length === 0) {
      // No specific days chosen → repeat on the same weekday as creation date
      const base = new Date(item.date + "T00:00:00");
      const baseMonBased = (base.getDay() + 6) % 7;
      const d = new Date(dateStr + "T00:00:00");
      const dMonBased = (d.getDay() + 6) % 7;
      return baseMonBased === dMonBased;
    }
    const d = new Date(dateStr + "T00:00:00");
    const monBased = (d.getDay() + 6) % 7;
    return days.includes(monBased);
  }

  if (repeat === "Monthly") {
    const dates = item.repeatDates || [];
    if (dates.length === 0) {
      // No specific dates chosen → repeat on same day-of-month as creation
      const base = new Date(item.date + "T00:00:00");
      const d = new Date(dateStr + "T00:00:00");
      return base.getDate() === d.getDate();
    }
    const d = new Date(dateStr + "T00:00:00");
    return dates.includes(d.getDate());
  }

  return item.date === dateStr;
}

// Returns true if a task (which uses dueDate/startDate) should appear on dateStr,
// accounting for the new frequencyType fields set via QuickAdd.
export function _computeTaskOccurringOn(task, dateStr) {
  // remindAnchor is only ever set (by the add/edit panel) on a one-time task
  // whose start and due dates genuinely differ — that's the only case where
  // "which date" is actually ambiguous, so it's the only case this branches
  // on. Everything else keeps the original startDate-first default untouched.
  const isOnceLike = !task.frequencyType || task.frequencyType === "once";
  const base = (isOnceLike && task.remindAnchor === "due" && task.dueDate)
    ? task.dueDate
    : (task.startDate || task.dueDate);
  if (!base) return false;
  if (dateStr < base) return false;
  if (task.repeatEnd && dateStr > task.repeatEnd) return false; // past the recurrence end date

  const ft = task.frequencyType;
  if (!ft || ft === "once") return base === dateStr;

  if (ft === "daily") return true;
  if (ft === "interval") return isIntervalDueFrom(task, base, dateStr);

  if (ft === "specific" || ft === "count") {
    const d = new Date(dateStr + "T00:00:00");
    const monBased = (d.getDay() + 6) % 7; // 0=Mon…6=Sun
    if (ft === "specific") {
      const days = task.frequencyDays || [];
      return days.length === 0 ? true : days.includes(monBased);
    }
    return true; // count: advisory only, show every day
  }

  if (ft === "monthly") {
    const d = new Date(dateStr + "T00:00:00");
    if (task.monthlyMode === "count") return true;
    const dates = task.monthDates || [];
    return dates.length === 0 ? d.getDate() === new Date(base + "T00:00:00").getDate() : dates.includes(d.getDate());
  }

  if (ft === "yearly") {
    // Specific calendar dates recurring every year (mirrors habits' yearDates).
    const d = new Date(dateStr + "T00:00:00");
    const md = `${d.getMonth() + 1}-${d.getDate()}`;
    if (!(task.yearDates || []).includes(md)) return false;
    // "this year only" (yearlyOnce): match only in the base date's year.
    if (task.yearlyOnce) return d.getFullYear() === new Date(base + "T00:00:00").getFullYear();
    return true;
  }

  return base === dateStr;
}

// The clock time a task's reminders count back from. A one-time task whose
// reminders are anchored to its due date ("Remind before: Due date") uses its
// own DUE TIME (Jade, 2026-10-10) — "" when none was picked, which falls back to
// UNTIMED_REMINDER_HOUR like any untimed item. Tasks saved before due times
// existed have no `dueTime` field at all and keep counting back from their
// start time, exactly as they always did. Everything else uses the start time
// (or a per-occurrence override of it). Mirrored by taskReminderTime in
// lib/reminder-logic.js — keep the two in lockstep.
export function taskReminderTime(task, override) {
  const isOnceLike = !task.frequencyType || task.frequencyType === "once";
  if (isOnceLike && task.remindAnchor === "due" && task.dueDate && task.dueTime !== undefined && task.dueTime !== null) {
    return task.dueTime || "";
  }
  return (override && override.time) || task.time;
}

// ─── PER-OCCURRENCE TASK COMPLETION ───────────────────────────────────────────
// A one-time task ("once"/no frequencyType) is done via its single `done` flag,
// same as always. A RECURRING task tracks completion per-occurrence in
// taskOccurrenceOverrides (same storage already used for per-day subtask/notes-
// check state and drag-moved time) so finishing today's copy doesn't mark every
// future occurrence of the series done forever.
export function isTaskDoneOn(task, dateStr, overrides = {}) {
  const isRecurring = task.frequencyType && task.frequencyType !== "once";
  if (!isRecurring) return !!task.done;
  return !!overrides[`${task.id}:${dateStr}`]?.done;
}

// Toggle (or explicitly set, via forceDone) a task's done state for dateStr.
export function toggleTaskDoneOn(task, dateStr, setTasks, setTaskOccurrenceOverrides, forceDone) {
  const isRecurring = task.frequencyType && task.frequencyType !== "once";
  if (!isRecurring) {
    setTasks(prev => prev.map(t => {
      if (t.id !== task.id) return t;
      const nextDone = forceDone ?? !t.done;
      return { ...t, done: nextDone, completedAt: nextDone ? (t.completedAt || new Date().toISOString()) : null };
    }));
    return;
  }
  setTaskOccurrenceOverrides(prev => {
    const key = `${task.id}:${dateStr}`;
    const { done: _d, completedAt: _c, ...rest } = prev[key] || {};
    const nextDone = forceDone ?? !prev[key]?.done;
    if (!nextDone) {
      if (Object.keys(rest).length === 0) { const { [key]: _omit, ...others } = prev; return others; }
      return { ...prev, [key]: rest };
    }
    return { ...prev, [key]: { ...rest, done: true, completedAt: new Date().toISOString() } };
  });
}

// ─── LOCAL REMINDER NOTIFICATIONS (Tiers 1 & 2) ───────────────────────────────
// Scans tasks/events/reminders for anything whose reminder offset has just come
// due, so the in-app poller (below, in ClarityApp) can toast+play a sound
// (Tier 1) and ask the service worker to show a system notification (Tier 2).
// A "reminder"-type item (calReminders) has no offsets of its own — it fires
// at its own date/time, i.e. offset 0.
// NOTIF_GRACE_MS lives in ./constants/misc (imported above).

export function addDaysToDateStr(dateStr, n) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d + n);
  return fmt(dt);
}
export function dateTimeToDate(dateStr, timeStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [h, mi] = (timeStr || "00:00").split(":").map(Number);
  return new Date(y, m - 1, d, h || 0, mi || 0, 0, 0);
}

// Returns [{ key, title, body }] for reminders newly due as of `nowDate`, i.e.
// whose target notify-moment falls within the last NOTIF_GRACE_MS and hasn't
// already fired (per `firedMap`, keyed by `${itemId}:${occurrenceDate}:${offset}`).
// Pure aside from reading firedMap — callers own persisting it.
export function collectDueReminders({ tasks = [], calEvents = [], calReminders = [], taskOccurrenceOverrides = {}, calEventOccurrenceOverrides = {}, firedMap = {}, nowDate = new Date() }) {
  const due = [];
  const todayD = todayStr();

  const scanOne = (item, itemType) => {
    const offsets = itemType === "reminder" ? [0] : (Array.isArray(item.reminders) && item.reminders.length ? item.reminders : null);
    if (!offsets) return;
    const maxOffset = Math.max(...offsets, 0);
    const lookaheadDays = Math.ceil(maxOffset / 1440) + 1; // covers multi-day custom offsets
    for (let dayOffset = 0; dayOffset <= lookaheadDays; dayOffset++) {
      const dateStr = addDaysToDateStr(todayD, dayOffset);
      const occurs = itemType === "task" ? isTaskOccurringOn(item, dateStr) : isOccurringOn(item, dateStr);
      if (!occurs) continue;
      // A recurring task done for THIS occurrence is skipped; a one-time task's
      // single `done` flag still suppresses it outright (isTaskDoneOn covers both).
      if (itemType === "task" && isTaskDoneOn(item, dateStr, taskOccurrenceOverrides)) continue;
      const override = itemType === "task"
        ? taskOccurrenceOverrides[`${item.id}:${dateStr}`]
        : itemType === "event" ? calEventOccurrenceOverrides[`${item.id}:${dateStr}`] : null;
      const time = itemType === "task" ? taskReminderTime(item, override) : (override?.time || item.time);
      const occurAt = dateTimeToDate(dateStr, time || UNTIMED_REMINDER_HOUR);
      for (const off of offsets) {
        const offMin = Number(off) || 0;
        if (!time && offMin < DAY_MINUTES) continue; // see UNTIMED_REMINDER_HOUR
        const target = new Date(occurAt.getTime() - offMin * 60000);
        // Keyed on the actual target moment (not just date+offset) so editing an
        // item's time/reminders produces a fresh key and re-fires — a stale key
        // from the old time would otherwise look "already fired" and get skipped.
        const key = `${item.id}:${target.getTime()}`;
        if (firedMap[key]) continue;
        const deltaMs = nowDate.getTime() - target.getTime();
        if (deltaMs >= 0 && deltaMs <= NOTIF_GRACE_MS) {
          due.push({
            key, itemType, itemId: item.id,
            title: item.title || "(untitled)",
            // Day/week offsets read as "Tomorrow"/"In 2 weeks" rather than
            // "Starting in 24 hr", and an untimed item isn't "starting" at a
            // particular moment at all — it's just due that day.
            body: offMin >= DAY_MINUTES
              ? (time ? `Starts in ${reminderLabel(offMin).replace(" before", "")}` : `Due in ${reminderLabel(offMin).replace(" before", "")}`)
              : offMin === 0 ? "Starting now" : offMin % 60 === 0 ? `Starting in ${offMin / 60} hr` : `Starting in ${offMin} min`,
            alertType: item.reminderType === "alarm" ? "alarm" : "notification",
            alarmSound: item.alarmSound || DEFAULT_ALARM_SOUND,
            alarmSoundUri: item.alarmSoundUri || undefined,
            deltaMs, // diagnostic: how many ms past the target moment this fired at
          });
        }
      }
    }
  };

  tasks.forEach(t => scanOne(t, "task"));
  calEvents.forEach(e => scanOne(e, "event"));
  calReminders.forEach(r => scanOne(r, "reminder"));
  return due;
}

// ─── UPCOMING NATIVE ALARMS/NOTIFICATIONS (Android AlarmManager sync) ────────
// Unlike collectDueReminders (which asks "is anything due right now?", meant
// for the in-app poller), this asks "what reminders of this alert type will
// fire in the next `lookaheadDays`?" — so the native side can pre-schedule
// AlarmManager wake-ups while the app itself isn't running. Shared by
// collectUpcomingAlarms (reminderType === "alarm") and
// collectUpcomingNotifications (everything else) below.
// lookaheadDays default of 35 (not 21) covers the 4-week-before reminder cap
// in ReminderPicker's day/week stepper, plus a week of margin.
function _collectUpcomingByAlertType(wantAlarm, { tasks = [], calEvents = [], calReminders = [], taskOccurrenceOverrides = {}, calEventOccurrenceOverrides = {}, lookaheadDays = 35, maxCount = 300, fromDate = new Date() }) {
  const upcoming = [];
  const fromMs = fromDate.getTime();
  const untilMs = fromMs + lookaheadDays * 86400000;
  const fromDateStr = fmt(fromDate);

  const scanOne = (item, itemType) => {
    const isAlarm = item.reminderType === "alarm";
    if (isAlarm !== wantAlarm) return;
    const offsets = itemType === "reminder" ? [0] : (Array.isArray(item.reminders) && item.reminders.length ? item.reminders : null);
    if (!offsets) return;
    for (let dayOffset = 0; dayOffset <= lookaheadDays; dayOffset++) {
      const dateStr = addDaysToDateStr(fromDateStr, dayOffset);
      const occurs = itemType === "task" ? isTaskOccurringOn(item, dateStr) : isOccurringOn(item, dateStr);
      if (!occurs) continue;
      if (itemType === "task" && isTaskDoneOn(item, dateStr, taskOccurrenceOverrides)) continue;
      const override = itemType === "task"
        ? taskOccurrenceOverrides[`${item.id}:${dateStr}`]
        : itemType === "event" ? calEventOccurrenceOverrides[`${item.id}:${dateStr}`] : null;
      const time = itemType === "task" ? taskReminderTime(item, override) : (override?.time || item.time);
      const occurAt = dateTimeToDate(dateStr, time || UNTIMED_REMINDER_HOUR);
      for (const off of offsets) {
        const offMin = Number(off) || 0;
        if (!time && offMin < DAY_MINUTES) continue; // see UNTIMED_REMINDER_HOUR
        const triggerAt = occurAt.getTime() - offMin * 60000;
        if (triggerAt < fromMs || triggerAt > untilMs) continue;
        upcoming.push({
          key: `${item.id}:${triggerAt}`,
          title: item.title || "(untitled)",
          body: itemType === "task" ? "Task" : itemType === "event" ? "Event" : "Reminder",
          triggerAt,
          alarmSound: item.alarmSound || DEFAULT_ALARM_SOUND,
          alarmSoundUri: item.alarmSoundUri || undefined,
        });
      }
    }
  };

  tasks.forEach(t => scanOne(t, "task"));
  calEvents.forEach(e => scanOne(e, "event"));
  calReminders.forEach(r => scanOne(r, "reminder"));
  upcoming.sort((a, b) => a.triggerAt - b.triggerAt);
  return upcoming.slice(0, maxCount);
}

// Only items with reminderType === "alarm" — see AlarmSchedulerPlugin.
export function collectUpcomingAlarms(opts) {
  return _collectUpcomingByAlertType(true, opts);
}

// Everything else (reminderType unset or "notification") — see
// NotificationSchedulerPlugin. No alarmSound distinction needed (plain
// notifications always use the one fixed sound), but keeping the same
// shape as collectUpcomingAlarms for a single native scheduling pattern.
export function collectUpcomingNotifications(opts) {
  return _collectUpcomingByAlertType(false, opts);
}
